import { FormControl, MenuItem, Radio, Select } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import type { NewRequisitionForm } from "../../recoil/types";
import { themePalette } from "../../theme";
import {
  StyledSectionHeadingClinical,
  UnityCompleteSelectRoot,
} from "./NewRequisition.styles";
import {
  getUnityCompleteFormControlSx,
  unityCompleteMenuItemSx,
  unityCompleteMenuRadioSx,
} from "./testPanelStyles";
import {
  getAvailableUnityCompleteBundleOptions,
  matchUnityCompleteBundle,
  UNITY_COMPLETE_BUNDLES,
  type UnityCompleteBundleKey,
} from "./unityCompleteBundles";

type UnityCompleteBundleSelectProps = {
  form: NewRequisitionForm;
  disabled: boolean;
  onSelect: (key: UnityCompleteBundleKey) => void;
};

const MENU_VIEWPORT_GUTTER = 16;

const UnityCompleteBundleSelect = ({
  form,
  disabled,
  onSelect,
}: UnityCompleteBundleSelectProps) => {
  const selectedKey = matchUnityCompleteBundle(form);
  const availableOptions = getAvailableUnityCompleteBundleOptions(form);
  const selectValue =
    selectedKey && availableOptions.some((option) => option.key === selectedKey)
      ? selectedKey
      : "";

  const handleChange = (event: SelectChangeEvent) => {
    const next = event.target.value;
    if (next === "") {
      return;
    }
    onSelect(next as UnityCompleteBundleKey);
  };

  const renderValue = (selected: unknown) => {
    if (!selected) {
      return "Select One";
    }
    return UNITY_COMPLETE_BUNDLES[selected as UnityCompleteBundleKey].label;
  };

  return (
    <UnityCompleteSelectRoot>
      <StyledSectionHeadingClinical>
        Unity Complete
      </StyledSectionHeadingClinical>
      <FormControl
        sx={getUnityCompleteFormControlSx(disabled)}
        fullWidth
        size="medium"
        disabled={disabled}
      >
        <Select
          id="unity-complete-bundle-select"
          autoWidth
          displayEmpty
          value={selectValue}
          onChange={handleChange}
          renderValue={renderValue}
          sx={{ backgroundColor: themePalette.white }}
          MenuProps={{
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "left",
            },
            marginThreshold: MENU_VIEWPORT_GUTTER,
          }}
          inputProps={{
            "aria-label": "Unity Complete bundle",
          }}
        >
          {availableOptions.map((option) => (
            <MenuItem
              key={option.key}
              value={option.key}
              sx={unityCompleteMenuItemSx}
            >
              <Radio
                checked={selectValue === option.key}
                disableRipple
                tabIndex={-1}
                inputProps={{ "aria-hidden": true }}
                sx={unityCompleteMenuRadioSx}
              />
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </UnityCompleteSelectRoot>
  );
};

export default UnityCompleteBundleSelect;
